import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";

// ── Helpers ───────────────────────────────────────────────────────────────────
const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
};

const clean = (snap) => ({ id: snap.id, ...snap.data() });

// ── Blog Posts ────────────────────────────────────────────────────────────────
export const blogService = {
  async getAll(publishedOnly = true) {
    let q = query(collection(db, "blogPosts"), orderBy("createdAt", "desc"));
    if (publishedOnly) q = query(collection(db, "blogPosts"), where("published", "==", true), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(clean);
  },

  async getById(id) {
    const snap = await getDoc(doc(db, "blogPosts", id));
    return snap.exists() ? clean(snap) : null;
  },

  async create(data) {
    return await addDoc(collection(db, "blogPosts"), {
      title: "",
      excerpt: "",
      content: "",
      category: "Product",
      author: "",
      readTime: "5 min",
      published: false,
      featured: false,
      tags: [],
      coverColor: "#ea3d95",
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  async update(id, data) {
    await updateDoc(doc(db, "blogPosts", id), { ...data, updatedAt: serverTimestamp() });
  },

  async delete(id) {
    await deleteDoc(doc(db, "blogPosts", id));
  },
};

// ── Documentation Articles ────────────────────────────────────────────────────
export const docsService = {
  async getAll() {
    const q = query(collection(db, "docs"), orderBy("category"), orderBy("order", "asc"));
    const snap = await getDocs(q);
    return snap.docs.map(clean);
  },

  async getById(id) {
    const snap = await getDoc(doc(db, "docs", id));
    return snap.exists() ? clean(snap) : null;
  },

  async create(data) {
    return await addDoc(collection(db, "docs"), {
      title: "",
      category: "Getting Started",
      content: "",
      order: 0,
      readTime: "5 min",
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  async update(id, data) {
    await updateDoc(doc(db, "docs", id), { ...data, updatedAt: serverTimestamp() });
  },

  async delete(id) {
    await deleteDoc(doc(db, "docs", id));
  },
};

// ── Webinars ──────────────────────────────────────────────────────────────────
export const webinarService = {
  async getAll() {
    const q = query(collection(db, "webinars"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(clean);
  },

  async create(data) {
    return await addDoc(collection(db, "webinars"), {
      title: "",
      presenter: "",
      date: "",
      time: "",
      duration: "60 min",
      status: "upcoming",
      tags: [],
      registeredCount: 0,
      viewCount: 0,
      accentColor: "#ea3d95",
      registrationUrl: "",
      price: 0,            // 0 = free registration
      bodyHtml: "",        // rich description shown on the card / detail
      ...data,
      createdAt: serverTimestamp(),
    });
  },

  async update(id, data) {
    await updateDoc(doc(db, "webinars", id), data);
  },

  async delete(id) {
    await deleteDoc(doc(db, "webinars", id));
  },

  // Seat registration / purchase intent. Payment (for paid webinars) is settled
  // separately; this records the registration and the amount owed.
  async register(webinar, { name, email, note }) {
    return await addDoc(collection(db, "webinarRegistrations"), {
      webinarId: webinar.id,
      webinarTitle: webinar.title,
      name: name || "",
      email: email || "",
      note: note || "",
      price: webinar.price || 0,
      paid: false,
      registeredAt: serverTimestamp(),
    });
  },
};

// ── Case Studies ──────────────────────────────────────────────────────────────
export const caseStudyService = {
  async getAll() {
    const q = query(collection(db, "caseStudies"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(clean);
  },

  async getById(id) {
    const snap = await getDoc(doc(db, "caseStudies", id));
    return snap.exists() ? clean(snap) : null;
  },

  async create(data) {
    return await addDoc(collection(db, "caseStudies"), {
      company: "",
      industry: "Finance",
      headline: "",
      metric: "",
      metricLabel: "",
      summary: "",
      tags: [],
      accentColor: "#ea3d95",
      featured: false,
      ...data,
      createdAt: serverTimestamp(),
    });
  },

  async update(id, data) {
    await updateDoc(doc(db, "caseStudies", id), data);
  },

  async delete(id) {
    await deleteDoc(doc(db, "caseStudies", id));
  },
};

// ── Assessment Leads ──────────────────────────────────────────────────────────
export const assessmentService = {
  async saveLead(data) {
    return await addDoc(collection(db, "assessmentLeads"), {
      ...data,
      submittedAt: serverTimestamp(),
    });
  },

  async getAll() {
    const q = query(collection(db, "assessmentLeads"), orderBy("submittedAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(clean);
  },
};

export { toDate };
